<script lang="ts">
  import { nrkCheck } from '@nrk/core-icons';
  import type { Alternative } from '@pin/schemas/client-state-response';
  import { clientState } from '../util/clientStateStore';
  import { i18n } from '../util/i18n/i18n';
  import { trackEvent } from '../util/snowplow';
  import { votingSlipTooltip } from '../util/tooltipstore';
  import Tooltip from './Tooltip.svelte';

  export let alternative: Alternative;
  export let addVote: ((alternativeId: string) => void) | undefined = undefined;
  export let votes: string[] | undefined = undefined;
  export let disabled = false;
  export let submitted = false;

  const imageStyle: 'rounded' | 'portrait' | 'square' = 'rounded';

  const onClick = (alternativeId: string) => () => {
    if (!addVote) {
      trackEvent({ action: 'click:pre-open-select' });
    } else if (!disabled) {
      addVote(alternativeId);
    } else {
      trackEvent({ action: 'click:disabled-select' });
    }
    if (!($clientState.type === 'open' || $clientState.type === 'closing')) {
      votingSlipTooltip.set({
        id: alternative.title,
        message: 'Avstemningen har ikke åpnet enda',
      });
    } else {
      votingSlipTooltip.set(undefined);
    }
  };

  $: voteCounter = votes?.filter((vote) => vote === alternative.id) ?? [];
</script>

<li class:voteable={addVote != undefined}>
  <picture>
    <source srcset={alternative.image.src} type="image/webp" />
    <source srcset={alternative.image.fallbackSrc} type="image/jpg" />
    <img
      class={imageStyle}
      src="{alternative.image.fallbackSrc}.jpg"
      alt={alternative.image.alt}
    />
  </picture>
  <div class="content">
    <div>
      <h3
        data-title={alternative.title}
        style="--title-length: {alternative.title.length};"
      >
        {alternative.title}
      </h3>
      {#if alternative.description}
        <div>{alternative.description}</div>
      {/if}
    </div>

    {#if !submitted}
      <div class="vote-container">
        <Tooltip id={alternative.title}>
          <button
            class="secondary"
            class:disabled={disabled || !addVote}
            on:click={onClick(alternative.id)}
            aria-disabled={disabled || !addVote}
            data-test-id="select-alternative"
          >
            {i18n('poll.choose')}</button
          >
        </Tooltip>
      </div>
    {:else}
      <div
        class="votes"
        aria-label={i18n('aria-live.vote-count', {
          values: { voteCount: voteCounter.length },
        })}
      >
        {#each voteCounter as _vote}
          <div class="vote-checkmark">
            <!-- eslint-disable-next-line svelte/no-at-html-tags -->
            {@html nrkCheck}
          </div>
        {/each}
      </div>
    {/if}
  </div>
</li>

<style>
  li {
    display: flex;
    width: 100%;
    max-width: 21rem;
    margin: var(--spacing-4) 0;
  }

  @media (min-width: 50rem) {
    li {
      margin-top: var(--spacing-24);
      max-width: 48rem;
    }
  }

  img {
    width: 5rem;
    height: 5rem;
    flex-shrink: 0;
    object-fit: cover;
  }

  img.rounded {
    border-radius: 9999px;
  }

  img.portrait {
    height: 7rem;
  }

  .content {
    margin-left: var(--spacing-4);
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding: var(--spacing-10);
    color: var(--color-alternative-text);
    background-color: var(--color-alternative-bg);
  }

  button {
    margin-top: var(--spacing-8);
    display: block;
    min-width: 10rem;
    width: 100%;
  }

  .votes {
    margin-top: var(--spacing-8);
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    height: 2.5625rem;
  }

  .vote-checkmark {
    display: inline-flex;
    font-size: 1.125rem;
    background-color: var(--color-button-secondary-bg);
    color: var(--color-button-secondary-text);
    margin-left: var(--spacing-8);
    border-radius: 9999px;
  }

  @media (min-width: 50rem) {
    li.voteable {
      transition: transform 0.2s cubic-bezier(0.12, 0, 0, 1);
    }
    :global(.no-touch) li.voteable:hover {
      transform: translateX(var(--spacing-4));
    }

    img.portrait {
      width: 7rem;
      height: 9.8rem;
    }

    img.rounded,
    img.square {
      width: 7rem;
      height: 7rem;
    }

    .content {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      padding: var(--spacing-16);
      transition: background-color 0.5s cubic-bezier(0.12, 0, 0, 1);
    }

    :global(.no-touch) li.voteable:hover .content {
      background-color: var(--color-alternative-bg-hover);
    }

    button,
    .votes {
      width: 7rem;
      margin: 0;
    }
  }
</style>
